import { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  MapPin,
  Bed,
  ArrowLeft,
  Sun,
  Activity,
  History,
  Ticket,
} from 'lucide-react';
import { useEstate } from '../hooks/useEstates';
import { useTelegram } from '../hooks/useTelegram';
import { ImageGallery, Button, LoadingSpinner } from '../components';

export function EstatePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showBackButton, hideBackButton, onBackButtonClick } = useTelegram();

  const { data: estate, isLoading, error } = useEstate(id || '');

  // Handle back button
  useEffect(() => {
    showBackButton();
    const cleanup = onBackButtonClick(() => {
      navigate('/');
    });

    return () => {
      hideBackButton();
      cleanup();
    };
  }, [showBackButton, hideBackButton, onBackButtonClick, navigate]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !estate) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6 text-center">
        <div>
          <h1 className="font-serif text-2xl text-foreground mb-4">Castle not found</h1>
          <Button variant="glass" onClick={() => navigate('/')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Browse
          </Button>
        </div>
      </div>
    );
  }

  const BackLink = () => (
    <button
      onClick={() => navigate('/')}
      className="inline-flex items-center gap-2 px-3 py-2 text-foreground/90 hover:text-foreground hover:bg-white/10 rounded-sm transition-all"
    >
      <ArrowLeft className="w-5 h-5" />
      <span className="text-sm font-medium tracking-wide">Back to Castles</span>
    </button>
  );

  return (
    <div className="pb-12 animate-fade-in text-foreground">
      {/* Image gallery */}
      <ImageGallery images={estate.images} name={estate.name} />

      <div className="mx-auto max-w-6xl px-6 pt-6 space-y-10">
        {/* Top back */}
        <BackLink />

        {/* Header */}
        <header className="flex flex-col gap-3">
          <h1 className="font-serif text-3xl md:text-4xl font-medium text-foreground">
            {estate.name}
          </h1>
          <div className="text-slate-300 tracking-wide uppercase text-xs md:text-sm flex items-center">
            <MapPin className="mr-2 h-4 w-4" />
            {estate.location}
          </div>
        </header>

        {/* Action button - mirrors web-2 Book this property */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Link to={`/booking/${estate.id}`} className="flex-1">
            <Button
              variant="primary"
              className="w-full font-serif text-base px-6 py-4"
            >
              <Ticket className="w-5 h-5" />
              Book this property
            </Button>
          </Link>
        </div>

        {/* Property Details Sections */}
        <div className="flex flex-col gap-10">
          {/* Summary */}
          {estate.summary && (
            <section>
              <h2 className="font-serif text-2xl text-foreground mb-3 flex items-center gap-3">
                <Sun className="w-5 h-5" />
                Summary
              </h2>
              <p className="text-slate-300 leading-relaxed text-base md:text-lg">
                {estate.summary}
              </p>
            </section>
          )}

          {/* Bedrooms */}
          {estate.bedrooms > 0 && (
            <section>
              <h2 className="font-serif text-2xl text-foreground mb-3 flex items-center gap-3">
                <Bed className="w-5 h-5" />
                Bedrooms
              </h2>
              <p className="text-slate-300 text-base md:text-lg">
                {estate.bedrooms} Bedrooms
              </p>
            </section>
          )}

          {/* Amenities */}
          {estate.amenities.length > 0 && (
            <section>
              <h2 className="font-serif text-2xl text-foreground mb-3 flex items-center gap-3">
                <Sun className="w-5 h-5" />
                Amenities
              </h2>
              <ul className="list-disc pl-5 space-y-2 text-slate-300 text-base md:text-lg">
                {estate.amenities.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </section>
          )}

          {/* Activities */}
          {estate.activities.length > 0 && (
            <section>
              <h2 className="font-serif text-2xl text-foreground mb-3 flex items-center gap-3">
                <Activity className="w-5 h-5" />
                Activities
              </h2>
              <ul className="list-disc pl-5 space-y-2 text-slate-300 text-base md:text-lg">
                {estate.activities.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </section>
          )}

          {/* Historical significance */}
          {estate.historical_significance && (
            <section>
              <h2 className="font-serif text-2xl text-foreground mb-3 flex items-center gap-3">
                <History className="w-5 h-5" />
                Historical Significance
              </h2>
              <p className="text-slate-300 leading-relaxed text-base md:text-lg">
                {estate.historical_significance}
              </p>
            </section>
          )}
        </div>

        {/* Bottom action */}
        <div className="pt-2">
          <Link to={`/booking/${estate.id}`} className="block">
            <Button
              variant="primary"
              className="w-full font-serif text-base px-6 py-4"
            >
              <Ticket className="w-5 h-5" />
              Book this property
            </Button>
          </Link>
        </div>

        {/* Bottom back */}
        <BackLink />
      </div>
    </div>
  );
}
